import { connectorApiUrl } from '@/config'
import router from '@/router'
import { getCurrentUserInfo } from '@/services/auth'
import { errorIfInvalidResponse } from '@/api/errors'
import firebase from 'firebase/app'
import 'firebase/auth'
import { ActionTree, GetterTree, Module, MutationTree } from 'vuex'
import { initRequestHelper, isAlreadyPending } from '../root'
import { RootState } from '../types/root'
import { MemberWithTeamUuid, TeamState, MemberRemoveData, Invite } from '../types/team'

import {
  apiGetInvites,
  apiGetTeams,
  apiCancelInvite,
  apiRemoveMember,
  apiInviteTeamMember,
  apiAcceptInvite,
  apiGetShareeUuidsForResource,
} from '@/api/team'
import { keys2Camel } from '@/services/objects'

const getDefaultState = (): TeamState => {
  return {
    invites: null,
    members: null,
    defaultTeamUuid: null,
    currentResourceMembers: null,
  }
}

const moduleState = getDefaultState()

export const moduleGetters: GetterTree<TeamState, RootState> = {
  defaultTeamUuid(state) {
    return state.defaultTeamUuid
  },
  members(state) {
    return state.members
  },
  //TODO: this is hotfix -> not to show deleted members on frontend
  activeMembers(_state, getters) {
    return getters.members.filter(
      (member: MemberWithTeamUuid) => !(member.displayName === 'deleted'),
    )
  },
  invites(state) {
    return state.invites
  },
  getInvitesRequest(_state, _getters, rootState) {
    return rootState.requests.getInvites
  },
  getTeamMembersRequest(_state, _getters, rootState) {
    return rootState.requests.getTeamMembers
  },
  loadingCurrentResourceMembers(state) {
    return state.currentResourceMembers === null
  },
  currentResourceMembers(state) {
    return state.currentResourceMembers
  },
  //TODO: this is hotfix -> not to show deleted members on frontend
  activeCurrentResourceMembers(_state, getters) {
    return getters.currentResourceMembers?.filter((email: string) => !!email)
  },
  assignResourceRequest(_state, _getters, rootState) {
    return rootState.requests.assignResource
  },
  assignResourceRequestPending(_state, getters) {
    return isAlreadyPending(getters.assignResourceRequest)
  },
  acceptInviteRequest(_state, _getters, rootState) {
    return rootState.requests.acceptInvite
  },
  sendInviteRequest(_state, _getters, rootState) {
    return rootState.requests.inviteTeamMember
  },
  cancelInviteRequest(_state, _getters, rootState) {
    return rootState.requests.cancelInvite
  },
  removeMemberRequest(_state, _getters, rootState) {
    return rootState.requests.removeMember
  },

  loading(_state, getters): boolean {
    return getters.listInvitesRequestPending || getters.listMembersRequestPending
  },
  cancelInviteRequestPending(_state, getters): boolean {
    return isAlreadyPending(getters.cancelInviteRequest)
  },
  listMembersRequestPending(_state, getters): boolean {
    return isAlreadyPending(getters.getTeamMembersRequest)
  },
  removeMemberRequestPending(_state, getters): boolean {
    return isAlreadyPending(getters.removeMemberRequest)
  },

  listInvitesRequestPending(_state, getters): boolean {
    return isAlreadyPending(getters.getInvitesRequest)
  },

  sendInviteRequestPending(_state, getters): boolean {
    return isAlreadyPending(getters.sendInviteRequest)
  },
}

export const moduleMutations: MutationTree<TeamState> = {
  setDefaultTeamUuid(state, teamUuid) {
    state.defaultTeamUuid = teamUuid
  },
  setInvites(state, invites: Invite[]) {
    state.invites = invites
  },
  SET_TEAM_MEMBERS(state, members: MemberWithTeamUuid[]) {
    // Map from response to internal representation
    state.members = members
  },
  setCurrentResourceMembers(state, memberEmails: string[] | null) {
    state.currentResourceMembers = memberEmails
  },
  resetState(state) {
    Object.assign(state, getDefaultState())
  },
}

/**
 * Filters out elements of A it has common with B
 * @param a Array of strings A
 * @param b Array of strings B
 * @returns array of Array A elements not existing in Array B
 */
const difference = (a: string[], b: string[]) => {
  return a.filter((x) => b.indexOf(x) < 0)
}

export const moduleActions: ActionTree<TeamState, RootState> = {
  async loadInvitesAndMembers({ dispatch, getters }) {
    if (getters.members === null || getters.invites === null)
      Promise.all([dispatch('loadInvites'), dispatch('loadMembers')])
  },

  async loadInvites({ commit, getters }, force = false) {
    if (!force) {
      if (isAlreadyPending(getters.getInvitesRequest)) return
    }
    const [setSuccess, setFailure] = initRequestHelper(commit, 'getInvites')
    try {
      const invites = await apiGetInvites()

      commit('setInvites', keys2Camel(invites))
      setSuccess()
    } catch (err: any) {
      setFailure(err.message)
      throw err
    }
  },
  async loadMembers({ commit, getters }) {
    if (isAlreadyPending(getters.getTeamMembersRequest)) return
    const [setSuccess, setFailure] = initRequestHelper(commit, 'getTeamMembers')

    try {
      const rawGetTeamsResponse = await apiGetTeams()
      const teams = keys2Camel(rawGetTeamsResponse)

      const members: MemberWithTeamUuid[] = []
      const currentUserUuid = getCurrentUserInfo().userId

      for (const team of teams) {
        for (const member of team.members) {
          if (member.memberUuid !== currentUserUuid) {
            members.push({
              ...member,
              teamUuid: team.uuid,
            })
          }
        }
      }
      commit('SET_TEAM_MEMBERS', members)
      setSuccess()
    } catch (err: any) {
      setFailure(err.message)
      throw err
    }
  },

  async inviteTeamMembers({ commit, dispatch }, emails: string[]) {
    const [setSuccess, setFailure] = initRequestHelper(commit, 'inviteTeamMember')
    try {
      for (const email of emails) {
        await apiInviteTeamMember(email)
      }
      await dispatch('loadInvites')

      setSuccess()
      router.replace('/team')
    } catch (err: any) {
      setFailure(err.message)
      throw err
    }
  },

  async removeMember({ commit, dispatch, getters }, membersToRemove: MemberRemoveData[]) {
    if (isAlreadyPending(getters.removeMemberRequest)) return
    const [setSuccess, setFailure] = initRequestHelper(commit, 'removeMember')
    try {
      for (const member of membersToRemove) {
        const { teamUuid, memberUuid } = member
        await apiRemoveMember(teamUuid, memberUuid)
      }

      setSuccess()
      dispatch('loadMembers')
    } catch (e: any) {
      setFailure(e.message)
      throw e
    }
  },

  async cancelInvite({ commit, dispatch, getters }, invitesToDelete: string[]) {
    if (isAlreadyPending(getters.cancelInvite)) return
    const [setSuccess, setFailure] = initRequestHelper(commit, 'cancelInvite')
    try {
      for (const inviteUuid of invitesToDelete) {
        await apiCancelInvite(inviteUuid)
      }

      setSuccess()
      dispatch('loadInvites')
    } catch (e: any) {
      setFailure(e.message)
      throw e
    }
  },

  async acceptInvite({ commit }, secret) {
    const [setSuccess, setFailure] = initRequestHelper(commit, 'acceptInvite')
    try {
      await apiAcceptInvite(secret)
      setSuccess()
    } catch (err: any) {
      setFailure(err.message)
      throw err
    }
  },

  // tech-debt, should be in src/api/teams
  async assignResourceToMembers(
    { commit, dispatch },
    { resourceType, uuid, membersToAssignUuids },
  ) {
    const [setSuccess, setFailure] = initRequestHelper(commit, 'assignResource')
    try {
      const { currentUser } = firebase.auth()
      const idToken = await currentUser!.getIdToken()
      const route = resourceType === 'workstation' ? 'share_workstations' : 'share_drives'
      const sharedResourcesResponse = await fetch(`${connectorApiUrl}/${route}/${uuid}`, {
        headers: {
          'Content-Type': 'application/json',
          'User-Token': idToken,
        },
        method: 'GET',
      })
      await errorIfInvalidResponse(
        sharedResourcesResponse,
        'Failed to load information about sharees.',
      )

      const resourceJson = await sharedResourcesResponse.json()
      const membersIdsSharedWith: string[] = resourceJson || []

      const toShareUids = difference(membersToAssignUuids, membersIdsSharedWith)
      const toUnshareUids = difference(membersIdsSharedWith, membersToAssignUuids)
      for (const uid of toShareUids) {
        const response = await fetch(`${connectorApiUrl}/${route}/${uuid}/${uid}`, {
          headers: {
            'Content-Type': 'application/json',
            'User-Token': idToken,
          },
          method: 'POST',
        })
        await errorIfInvalidResponse(response, 'Failed to share.')
      }
      for (const uid of toUnshareUids) {
        const sharedResourcesResponse = await fetch(`${connectorApiUrl}/${route}/${uuid}/${uid}`, {
          headers: {
            'Content-Type': 'application/json',
            'User-Token': idToken,
          },
          method: 'DELETE',
        })
        await errorIfInvalidResponse(sharedResourcesResponse, 'Failed to unshare.')
      }
      await dispatch('loadInvitesAndMembers')
      setSuccess('Assignment updated!')
    } catch (err: any) {
      setFailure(err.message)
      throw err
    }
  },

  // tech-debt, should be in src/api/teams
  async getCurrentResourceMembers({ commit, dispatch, getters }, { resourceType, uuid }) {
    commit('setCurrentResourceMembers', null)
    const [setSuccess, setFailure] = initRequestHelper(commit, 'currentResourceMembers')
    try {
      await dispatch('loadMembers')
      const shareesUuids = await apiGetShareeUuidsForResource(resourceType, uuid)
      const members = getters.members

      const result: string[] = []
      for (const member of members) {
        if (shareesUuids.includes(member.memberUuid)) {
          result.push(member.memberUuid)
        }
      }
      commit('setCurrentResourceMembers', result)
      setSuccess()
    } catch (err: any) {
      setFailure(err.message)
      throw err
    }
  },
  async resetTeamState({ commit }) {
    commit('resetState')
  },
}

const team: Module<TeamState, RootState> = {
  namespaced: true,
  state: moduleState,
  getters: moduleGetters,
  mutations: moduleMutations,
  actions: moduleActions,
}
export default team
